import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import React, { useEffect, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import Button from '../../../App/Components/Button/Button'
import { TextInputLabel } from '../../../App/Components/Form/TextInput/style'
import TextInput from '../../../App/Components/Form/TextInput/TextInput'
import { useAppDispatch } from '../../../App/Hooks/reduxHooks'
import { IHICustomerDetails } from '../../interfaces/IHealthInsurance'
import { setHICustomerDetails } from '../../store/action'
import { ErrorMessage } from '../../style'

type Props = {
  prefillCustomerData?: IHICustomerDetails
}

const schema = yup.object({
  address: yup.string().required(),
  postalCode: yup.string().required(),
  city: yup.string().required(),
  country: yup.string().required(),
  healthInsuranceNumber: yup.string().required(),
  birthDay: yup
    .string()
    .test('DOB', 'Person should be older then 1 day.', value => {
      return moment().diff(moment(value), 'day') >= 1
    })
    .required(),
  extraInfo: yup.string().nullable(),
  gender: yup.string().oneOf(['M', 'F', 'X']).required(),
  bsnCode: yup.string().nullable(),
})
type IHICustomerFormData = yup.InferType<typeof schema>

const TextInputWithRef = React.forwardRef(
  ({ label, ...rest }: any, ref: React.Ref<HTMLInputElement>) => (
    <TextInput label={label} {...rest} inputRef={ref} />
  ),
)

const TranslateErrorMessage = ({ message }: { message?: string }) => {
  const { t } = useTranslation()
  if (!message) return null
  return <ErrorMessage>{t(message)}</ErrorMessage>
}

export const HealthInsuranceData = ({ prefillCustomerData }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<IHICustomerFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      address: prefillCustomerData?.address || '',
      postalCode: prefillCustomerData?.postalCode || '',
      city: prefillCustomerData?.city || '',
      country: prefillCustomerData?.country || '',
      healthInsuranceNumber: prefillCustomerData?.healthInsuranceNumber || '',
      bsnCode: '',
      birthDay: prefillCustomerData?.birthDay
        ? moment(prefillCustomerData.birthDay).format('DD-MM-YYYY')
        : moment().format('DD-MM-YYYY'),
      gender: prefillCustomerData?.gender || 'X',
      extraInfo: '',
    },
  })

  const addressRef = useRef<HTMLInputElement>(null)
  const postalCodeRef = useRef<HTMLInputElement>(null)
  const cityRef = useRef<HTMLInputElement>(null)
  const countryRef = useRef<HTMLInputElement>(null)
  const healthInsuranceNumberRef = useRef<HTMLInputElement>(null)
  const bsnCodeRef = useRef<HTMLInputElement>(null)
  const birthDayRef = useRef<HTMLInputElement>(null)
  const extraInfoRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setValue('address', prefillCustomerData?.address || '')
    setValue('postalCode', prefillCustomerData?.postalCode || '')
    setValue('city', prefillCustomerData?.city || '')
    setValue('country', prefillCustomerData?.country || '')
    setValue(
      'healthInsuranceNumber',
      prefillCustomerData?.healthInsuranceNumber || '',
    )
    setValue('birthDay', prefillCustomerData?.birthDay || '')
  }, [prefillCustomerData, setValue])

  const onSubmit = (data: IHICustomerFormData) => {
    dispatch(
      setHICustomerDetails({
        address: data.address,
        postalCode: data.postalCode,
        city: data.city,
        country: data.country,
        healthInsuranceNumber: data.healthInsuranceNumber,
        birthDay: moment(data.birthDay).format('YYYY-MM-DD'),
        extraInfo: data.extraInfo || null,
        gender: data.gender,
        isReadyForTable: true,
        bsnCode: data.bsnCode || null,
      }),
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="address"
        control={control}
        render={({ field }) => (
          <TextInputWithRef {...field} label={t('Address')} ref={addressRef} />
        )}
      />
      <TranslateErrorMessage message={errors.address?.message} />
      <Controller
        name="postalCode"
        control={control}
        render={({ field }) => (
          <TextInputWithRef
            {...field}
            label={t('Postal code')}
            ref={postalCodeRef}
          />
        )}
      />
      <TranslateErrorMessage message={errors.postalCode?.message} />
      <Controller
        name="city"
        control={control}
        render={({ field }) => (
          <TextInputWithRef {...field} label={t('City')} ref={cityRef} />
        )}
      />
      <TranslateErrorMessage message={errors.city?.message} />
      <Controller
        name="country"
        control={control}
        render={({ field }) => (
          <TextInputWithRef {...field} label={t('Country')} ref={countryRef} />
        )}
      />
      <TranslateErrorMessage message={errors.country?.message} />
      <Controller
        name="healthInsuranceNumber"
        control={control}
        render={({ field }) => (
          <TextInputWithRef
            {...field}
            label={t('Health insurance number')}
            ref={healthInsuranceNumberRef}
          />
        )}
      />
      <TranslateErrorMessage message={errors.healthInsuranceNumber?.message} />
      <Controller
        name="bsnCode"
        control={control}
        render={({ field }) => (
          <TextInputWithRef {...field} label={t('BSN code')} ref={bsnCodeRef} />
        )}
      />
      <TranslateErrorMessage message={errors.bsnCode?.message} />
      <Controller
        name="birthDay"
        control={control}
        render={({ field }) => (
          <TextInputWithRef
            type={'date'}
            {...field}
            label={t('BirthDay')}
            ref={birthDayRef}
          />
        )}
      />
      <TranslateErrorMessage message={errors.birthDay?.message} />
      <Controller
        name="gender"
        control={control}
        render={({ field }) => (
          <>
            <TextInputLabel
              color={'rgba(0,0,0,0.5)'}
              textAlign="left"
              background="#ffffff">
              {t('Gender')}
            </TextInputLabel>
            <select {...field}>
              <option value="M">M</option>
              <option value="F">F</option>
              <option value="X">X</option>
            </select>
          </>
        )}
      />
      <TranslateErrorMessage message={errors.gender?.message} />
      <Controller
        name="extraInfo"
        control={control}
        render={({ field }) => (
          <TextInputWithRef
            {...field}
            label={t('Extra info')}
            ref={extraInfoRef}
          />
        )}
      />
      <Button type="submit">{t('Submit')}</Button>
    </form>
  )
}
